import React from 'react';
import type { Experience } from '@personal-portfolio/contract';
import { useSiteContent } from '@personal-portfolio/queries';
import {
  Badge,
  Card,
  CardContent,
  CardTitle,
  CardHeader,
  Link,
  LinkButton,
} from '@/shared/ui';
import { ExternalLink, MapPin, Calendar } from 'lucide-react';

interface ExperienceCardProps {
  experience: Experience;
}

export const ExperienceCard: React.FC<ExperienceCardProps> = ({
  experience,
}) => {
  const { cards } = useSiteContent();
  const {
    slug,
    title,
    company,
    type,
    industry,
    location,
    workMode,
    description,
    summary,
    achievements,
    categories,
    logo,
    links,
    duration,
    projects,
  } = experience;

  const companyProfileLink = links?.find(
    (link) => link.type === 'company-profile',
  );

  return (
    <Card className="group scroll-mt-20 hover:shadow-md" id={slug}>
      <CardHeader className="space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            {logo && (
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg border">
                <img
                  src={logo.url}
                  alt={logo.alternativeText || company}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              </div>
            )}
            <div className="space-y-2">
              <CardTitle className="text-xl font-semibold tracking-tight">
                {companyProfileLink?.url ? (
                  <Link
                    href={companyProfileLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="decoration-muted-foreground/40 underline-offset-4 hover:underline"
                  >
                    {title}
                  </Link>
                ) : (
                  title
                )}
              </CardTitle>
            </div>
          </div>
        </div>

        <div className="-mt-2 flex flex-wrap items-center gap-4 font-mono text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            <span>{duration}</span>
          </div>
          {location && (
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              <span>{location}</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className="font-mono text-xs">
            {type}
          </Badge>
          {industry && (
            <Badge variant="outline" className="font-mono text-xs">
              {industry}
            </Badge>
          )}
          {workMode && (
            <Badge variant="outline" className="font-mono text-xs">
              {workMode}
            </Badge>
          )}
          {categories.map((category) => (
            <Badge
              key={category.id}
              variant="outline"
              className="font-mono text-xs"
            >
              {category.name}
            </Badge>
          ))}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {summary && (
          <div className="space-y-2">
            <h4 className="font-mono text-xs font-medium tracking-wider text-muted-foreground uppercase">
              {cards.summary}
            </h4>
            <p className="text-sm leading-relaxed break-words text-muted-foreground">
              {summary}
            </p>
          </div>
        )}

        <div className="space-y-2">
          <h4 className="font-mono text-xs font-medium tracking-wider text-muted-foreground uppercase">
            {cards.description}
          </h4>
          <div
            className="prose prose-sm max-w-none text-sm leading-relaxed break-words text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>

        {achievements && achievements.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-mono text-xs font-medium tracking-wider text-muted-foreground uppercase">
              {cards.keyAchievements}
            </h4>
            <div className="text-sm text-muted-foreground">
              <ul className="list-outside list-disc space-y-1 pl-5">
                {achievements.map((achievement) => (
                  <li key={achievement}>{achievement}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {projects && projects.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-mono text-xs font-medium tracking-wider text-muted-foreground uppercase">
              {cards.keyProjects}
            </h4>
            <div className="space-y-3">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="space-y-2 rounded-lg border p-3"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h5 className="text-sm font-medium">{project.title}</h5>
                      {project.summary && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {project.summary}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <Badge variant="outline" className="font-mono text-xs">
                        {project.type}
                      </Badge>
                      <Badge
                        variant={
                          project.status === 'completed'
                            ? 'default'
                            : 'secondary'
                        }
                        className="font-mono text-xs"
                      >
                        {project.status}
                      </Badge>
                    </div>
                  </div>

                  {project.description && (
                    <div className="text-xs text-muted-foreground">
                      {project.description}
                    </div>
                  )}

                  {project.links && project.links.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {project.links.map((link) => (
                        <LinkButton
                          key={link.id}
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {link.label}
                          <ExternalLink className="ml-1" />
                        </LinkButton>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {links && links.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {links.map((link) => (
              <LinkButton
                key={link.id}
                variant="outline"
                size="sm"
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.label}
                <ExternalLink className="ml-1" />
              </LinkButton>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
